import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Save, Upload, Waypoints } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { CheckboxGroup, GroupField, ProductListField } from "../components/FormFields";

export default function PublicForm() {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [params] = useSearchParams();
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const [draftEmail, setDraftEmail] = useState("");
  const [draftSent, setDraftSent] = useState(false);

  useEffect(() => {
    const t = params.get("draft");
    if (!t) return;
    api.get(`/public/drafts/${t}`)
      .then(({ data }) => {
        setValues(data.data || {});
        setDraftLoaded(true);
        if (data.email) setDraftEmail(data.email);
      })
      .catch(() => toast.error("Draft not found or expired"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDraft = async () => {
    if (!draftEmail.trim()) {
      toast.error("Enter your email to receive the resume link");
      return;
    }
    try {
      await api.post(`/public/forms/${formId}/drafts`, { email: draftEmail, data: values });
      setDraftSent(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    if (!form || !draftEmail) return;
    const ef = form.fields.find((f) => f.type === "email");
    if (ef) setValues((prev) => (prev[ef.id] ? prev : { ...prev, [ef.id]: draftEmail }));
  }, [form, draftEmail]);

  useEffect(() => {
    api.get(`/public/forms/${formId}`).then(({ data }) => setForm(data)).catch((e) => setError(apiError(e)));
  }, [formId]);

  const setValue = (id, v) => setValues((p) => ({ ...p, [id]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("data", JSON.stringify(values));
      Object.values(files).flat().forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/public/forms/${formId}/submit`, fd);
      setDone(data.message);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="form-error">
        <p className="text-neutral-500">{error}</p>
      </div>
    );
  if (!form)
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-400" data-testid="form-loading">Loading…</div>
    );

  if (form.closed)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6" data-testid="form-closed">
        <div className="max-w-md w-full bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center">
          <div className="flex items-center justify-center gap-2 text-neutral-500 mb-6">
            <Waypoints className="h-4 w-4" />
            <span className="text-xs uppercase tracking-widest">{form.business_name}</span>
          </div>
          <h1 className="text-3xl font-black" data-testid="form-closed-title">{form.title}</h1>
          <p className="mt-4 text-neutral-700 leading-relaxed" data-testid="form-closed-message">{form.closed_message}</p>
        </div>
      </div>
    );

  if (done)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6" data-testid="form-success">
        <div className="max-w-md text-center">
          <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
          <h1 className="mt-6 text-3xl font-black">Submission received</h1>
          <p className="mt-4 text-neutral-500 leading-relaxed" data-testid="form-success-message">{done}</p>
          <p className="mt-3 text-sm text-neutral-400">We've emailed you a secure link so you can view replies and continue the conversation anytime.</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-16 px-6" data-testid="public-form-page">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 text-neutral-400 mb-10">
          <Waypoints className="h-4 w-4" />
          <span className="text-xs uppercase tracking-widest" data-testid="form-business-name">{form.business_name}</span>
        </div>
        <h1 className="text-4xl font-black" data-testid="form-title">{form.title}</h1>
        {form.description && <p className="mt-3 text-neutral-500" data-testid="form-description">{form.description}</p>}
        {draftLoaded && (
          <p className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5" data-testid="draft-restored">
            Your saved draft has been restored.
          </p>
        )}
        <form onSubmit={submit} className="mt-10 space-y-8">
          {form.fields.map((field) => (
            <div key={field.id} className="space-y-2.5">
              <Label className="text-base" htmlFor={`field-${field.id}`}>
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </Label>
              {["text", "email", "phone", "number", "date"].includes(field.type) && (
                <Input
                  id={`field-${field.id}`}
                  type={field.type === "phone" ? "tel" : field.type}
                  placeholder={field.placeholder}
                  value={values[field.id] || ""}
                  onChange={(e) => setValue(field.id, e.target.value)}
                  required={field.required}
                  className="h-14 text-lg px-4 bg-white"
                  data-testid={`form-field-${field.id}`}
                />
              )}
              {field.type === "textarea" && (
                <Textarea
                  id={`field-${field.id}`}
                  placeholder={field.placeholder}
                  value={values[field.id] || ""}
                  onChange={(e) => setValue(field.id, e.target.value)}
                  required={field.required}
                  rows={5}
                  className="text-lg p-4 bg-white"
                  data-testid={`form-field-${field.id}`}
                />
              )}
              {field.type === "select" && (
                <Select value={values[field.id] || ""} onValueChange={(v) => setValue(field.id, v)}>
                  <SelectTrigger className="h-14 text-lg bg-white" data-testid={`form-field-${field.id}`}>
                    <SelectValue placeholder={field.placeholder || "Choose…"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {(field.options || []).map((o) => (
                      <SelectItem key={o} value={o} data-testid={`form-option-${field.id}-${o}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {field.type === "radio" && (
                <RadioGroup value={values[field.id] || ""} onValueChange={(v) => setValue(field.id, v)} data-testid={`form-field-${field.id}`}>
                  {(field.options || []).map((o) => (
                    <div key={o} className="flex items-center gap-3 bg-white border border-[#E5E5E5] rounded-xl px-4 py-3">
                      <RadioGroupItem value={o} id={`${field.id}-${o}`} data-testid={`form-radio-${field.id}-${o}`} />
                      <Label htmlFor={`${field.id}-${o}`} className="font-normal cursor-pointer">{o}</Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
              {field.type === "checkbox" && (field.options || []).length > 0 && (
                <CheckboxGroup
                  field={field}
                  value={values[field.id]}
                  onChange={(v) => setValue(field.id, v)}
                  testIdPrefix={`form-field-${field.id}`}
                />
              )}
              {field.type === "group" && (
                <GroupField
                  field={field}
                  value={values[field.id]}
                  onChange={(v) => setValue(field.id, v)}
                  testIdPrefix={`form-field-${field.id}`}
                />
              )}
              {field.type === "product_list" && (
                <ProductListField
                  field={field}
                  value={values[field.id]}
                  onChange={(v) => setValue(field.id, v)}
                  testIdPrefix={`form-field-${field.id}`}
                />
              )}
              {field.type === "checkbox" && !(field.options || []).length && (
                <div className="flex items-center gap-3 bg-white border border-[#E5E5E5] rounded-xl px-4 py-3">
                  <Checkbox
                    id={field.id}
                    checked={!!values[field.id]}
                    onCheckedChange={(v) => setValue(field.id, !!v)}
                    data-testid={`form-field-${field.id}`}
                  />
                  <Label htmlFor={field.id} className="font-normal cursor-pointer">{field.placeholder || "Yes"}</Label>
                </div>
              )}
              {field.type === "file" && (
                <label className="flex items-center gap-3 bg-white border border-dashed border-neutral-300 rounded-xl px-4 py-5 cursor-pointer hover:border-neutral-500 transition-colors duration-150" data-testid={`form-field-${field.id}`}>
                  <Upload className="h-5 w-5 text-neutral-400" />
                  <span className="text-sm text-neutral-500">
                    {files[field.id]?.length ? files[field.id].map((f) => f.name).join(", ") : field.placeholder || "Click to upload files"}
                  </span>
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => setFiles((p) => ({ ...p, [field.id]: Array.from(e.target.files || []) }))}
                    data-testid={`form-file-input-${field.id}`}
                  />
                </label>
              )}
            </div>
          ))}
          <Button type="submit" disabled={submitting} className="w-full rounded-full h-14 text-lg transition-transform duration-150 hover:-translate-y-0.5" data-testid="form-submit-button">
            {submitting ? "Submitting…" : "Submit"}
          </Button>
          <div className="pt-1">
            {!showDraft ? (
              <button
                type="button"
                onClick={() => {
                  const ef = form.fields.find((f) => f.type === "email");
                  if (ef && values[ef.id]) setDraftEmail(values[ef.id]);
                  setShowDraft(true);
                }}
                className="text-sm text-neutral-500 underline hover:text-neutral-900 flex items-center gap-1.5 mx-auto transition-colors duration-150"
                data-testid="save-draft-button"
              >
                <Save className="h-4 w-4" /> Save & finish later
              </button>
            ) : draftSent ? (
              <p className="text-sm text-emerald-700 text-center" data-testid="draft-sent">
                Resume link sent — check your inbox. It works for 7 days.
              </p>
            ) : (
              <div className="flex gap-2">
                <Input type="email" placeholder="Your email" value={draftEmail} onChange={(e) => setDraftEmail(e.target.value)} className="h-11" data-testid="draft-email-input" />
                <Button type="button" variant="outline" className="rounded-full shrink-0 h-11" onClick={saveDraft} data-testid="draft-send-button">
                  Email me a link
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
